import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

export interface UseCSVExportOptions<T> {
    /** Base filename (without extension or date) */
    filename: string;
    /** CSV column headers */
    headers: string[];
    /** Function to map each item to CSV row values */
    mapRow: (item: T) => (string | number | null | undefined)[];
}

export interface UseCSVExportReturn<T> {
    /** Download items as CSV file */
    downloadCSV: (items: T[]) => void;
}

/**
 * useCSVExport - Hook for standardized CSV export functionality
 * 
 * @example
 * const { downloadCSV } = useCSVExport({
 *   filename: 'gastos',
 *   headers: ['Fecha', 'Propiedad', 'Monto'],
 *   mapRow: (expense) => [expense.date, expense.property, expense.amount],
 * });
 * 
 * // Export selected or all items
 * const itemsToExport = selectedItems.length > 0 ? selected : all;
 * downloadCSV(itemsToExport);
 */
export function useCSVExport<T>({
    filename,
    headers,
    mapRow,
}: UseCSVExportOptions<T>): UseCSVExportReturn<T> {
    const { toast } = useToast();

    const downloadCSV = useCallback(
        (items: T[]) => {
            if (items.length === 0) {
                toast({
                    title: 'Sin datos',
                    description: 'No hay datos para exportar',
                    variant: 'destructive',
                });
                return;
            }

            // Map items to CSV rows
            const csvData = items.map((item) =>
                mapRow(item).map((field) => {
                    const value = field ?? 'N/A';
                    // Escape quotes and wrap in quotes
                    return `"${String(value).replace(/"/g, '""')}"`;
                })
            );

            // Build CSV content
            const csvContent = [
                headers.join(','),
                ...csvData.map((row) => row.join(',')),
            ].join('\n');

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            const dateStr = new Date().toISOString().split('T')[0];

            link.setAttribute('href', url);
            link.setAttribute('download', `${filename}_${dateStr}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast({
                title: 'CSV descargado',
                description: `Se han exportado ${items.length} registros`,
            });
        },
        [filename, headers, mapRow, toast]
    );

    return { downloadCSV };
}

export default useCSVExport;
